import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import AnimateOnScroll from './AnimateOnScroll.js';
import PageHero from './PageHero.js';
import heroOilgas from '../assets/hero-oilgas.jpg';

const OilGasPage = () => {
  return (
    <div className="bg-custom-earth">
      <div className="wood-container">
        <div className="min-h-screen flex flex-col">
          <Helmet>
            <title>Oil & Gas Vegetation Management BC | Boultbee</title>
            <meta name="description" content="Industrial herbicide application for oil refineries, pipeline corridors, tank farms, and pump stations in BC. Avetta certified with GPS-tracked precision application." />
            <link rel="canonical" href="https://www.weedmanager.com/industries/oil-gas" />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="Boultbee Vegetation Management" />
            <meta property="og:title" content="Oil & Gas Vegetation Management BC | Boultbee" />
            <meta property="og:description" content="Industrial herbicide application for oil refineries, pipeline corridors, tank farms, and pump stations in BC. Avetta certified with GPS-tracked precision application." />
            <meta property="og:url" content="https://www.weedmanager.com/industries/oil-gas" />
            <meta property="og:image" content="https://www.weedmanager.com/images/og-oilgas.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Oil & Gas Vegetation Management BC | Boultbee" />
            <meta name="twitter:description" content="Industrial herbicide application for oil refineries, pipeline corridors, tank farms, and pump stations in BC. Avetta certified with GPS-tracked precision application." />
            <meta name="twitter:image" content="https://www.weedmanager.com/images/og-oilgas.jpg" />
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.weedmanager.com/" },
                  { "@type": "ListItem", "position": 2, "name": "Oil & Gas Vegetation Control", "item": "https://www.weedmanager.com/industries/oil-gas" }
                ]
              })}
            </script>
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Service",
                "serviceType": "Oil & Gas Vegetation Management",
                "provider": {
                  "@type": "LocalBusiness",
                  "name": "Boultbee Vegetation Management",
                  "url": "https://www.weedmanager.com"
                },
                "areaServed": { "@type": "AdministrativeArea", "name": "British Columbia, Canada" },
                "description": "Industrial herbicide application for oil refineries, pipeline corridors, tank farms, and pump stations with Avetta certification and GPS-tracked precision application."
              })}
            </script>
          </Helmet>

          <PageHero
            title="Oil & Gas Vegetation Control"
            subtitle="Bare ground herbicide programs for refineries, pipeline corridors, and energy infrastructure"
            backgroundImage={heroOilgas}
            darkImage
            bgPosition="center bottom"
          />

          <AnimateOnScroll animation="fade-in-up">
            <p className="text-center text-lg md:text-xl font-serif italic text-green-900 max-w-3xl mx-auto px-6 py-6">Boultbee is Avetta certified with decades of experience operating in high-security oil and gas environments across British Columbia.</p>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Refinery & Tank Farm Bare Ground Control</h2>
              <p className="text-gray-700">Vegetation around tank farms, loading racks, and refinery perimeters creates fire hazards and operational risks. Our bare ground programs deliver total vegetation elimination using pre-emergent residual herbicides combined with post-emergent treatments for breakthrough growth. We maintain vegetation-free conditions around tank foundations, pipe racks, valve manifolds, and electrical equipment pads.</p>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Pipeline Corridor & Right-of-Way Management</h2>
              <p className="text-gray-700">Pipeline rights-of-way require vegetation management that maintains access for inspection, prevents root damage to infrastructure, and controls noxious weed species as required by regulators. Boultbee provides selective and bare ground herbicide programs for pipeline corridors, pump stations, compressor stations, and meter stations. Our programs are designed to meet NEB and BC OGC standards.</p>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Safety & Compliance</h2>
              <p className="text-gray-700">Operating in oil and gas environments demands rigorous safety compliance. Boultbee is Avetta certified and pre-qualified by major energy companies. Our crews hold First Aid and all required BC pesticide applicator certifications. We complete site-specific safety orientations and maintain all required safety documentation before any work begins.</p>
            </div>
          </AnimateOnScroll>

          {/* Other Industries We Serve */}
          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 text-center">Other Industries We Serve</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Link to="/industries/railroad" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 18h8M8 18l-2 3m10-3 2 3M6 6h12v8a4 4 0 0 1-4 4H10a4 4 0 0 1-4-4V6Zm0 0V4h12v2M9 10h6" /></svg>
                  <h3 className="font-bold text-gray-900">Railroads</h3>
                  <p className="text-gray-700 text-sm mt-1">High-rail application & ballast control</p>
                </Link>
                <Link to="/industries/municipal" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 21v-4.875c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125V21m0 0h4.5V8.25l-9-6.75-9 6.75V21h4.5" /></svg>
                  <h3 className="font-bold text-gray-900">Municipalities</h3>
                  <p className="text-gray-700 text-sm mt-1">Parks, sidewalks & public spaces</p>
                </Link>
                <Link to="/industries/utility" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" /></svg>
                  <h3 className="font-bold text-gray-900">Utilities</h3>
                  <p className="text-gray-700 text-sm mt-1">Substations, corridors & cable strips</p>
                </Link>
              </div>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur text-center">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Get a Quote</h2>
              <p className="text-gray-700 mb-5">From single-site refineries to province-wide pipeline programs, we deliver. <Link to="/services" className="text-green-700 underline hover:text-green-900">View all services</Link> or contact us for a site assessment.</p>
              <Link to="/contact" className="cta-button">
                Get a Quote
              </Link>
            </div>
          </AnimateOnScroll>
        </div>
      </div>
    </div>
  );
};

export default OilGasPage;
